import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import TidalWave from 'img/icon/tidal-wave.svg';

import LeafletMap from 'components/EcoEnvironmentalSurvey/LeafletMap/Content';
import Intro from 'components/Intro';
import Tab from 'components/Tab';
import { SWAL_TOAST } from 'data/common';
import { useAuthContext } from 'context/AuthContext';
import { useDownload } from 'hooks/useDownload';

const INTERTIDAL_YEAR_OPTIONS = [
  { value: '2002', label: '2002-2004' },
  { value: '2005', label: '2005-2007' },
  { value: '2008', label: '2008-2010' },
  { value: '2011', label: '2011-2013' },
  { value: '2014', label: '2014-2015' },
  { value: '2016', label: '2016-2017' },
  { value: '2018', label: '2018' },
  { value: '2019', label: '2019' },
  { value: '2020', label: '2020' },
  { value: '2021', label: '2021' },
  { value: '2022', label: '2022' },
  { value: '2023', label: '2023' },
  { value: '2024', label: '2024' },
  { value: '2025', label: '2025' },
];
const Content = () => {
  const [intertidalYear, setIntertidalYear] = useState(
    INTERTIDAL_YEAR_OPTIONS[0].value
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const intertidalYearIndex = INTERTIDAL_YEAR_OPTIONS.findIndex(
    (option) => option.value === intertidalYear
  );
  const intertidalYearLabel =
    INTERTIDAL_YEAR_OPTIONS[intertidalYearIndex]?.label || intertidalYear;
  const { auth } = useAuthContext();
  const { handleDownload } = useDownload();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  const pageData = {
    title: '潮間帶地形',
    icon: TidalWave,
    page: 'intertidal-topography',
    tabs: [
      {
        id: 1,
        title: '資料來源',
        content: (
          <>
            整合多時期衛星影像與潮汐資料，重建2002至2025年間潮間帶之歷年數值高程模型。
            資料集與方法說明可至{' '}
            <a
              href="https://data.depositar.io/en/dataset/intertidal-topography-in-changhua"
              className="e-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              depositar
            </a>{' '}
            頁面查看。
          </>
        ),
      },
      // {
      //   id: 2,
      //   title: '監測說明',
      //   content:
      //     '整合多時期衛星影像與潮汐資料，重建潮間帶歷年數值高程模型（10 公尺解析度），並計算地形變化趨勢。',
      // },
    ],
    url: 'intertidalTopography',
  };

  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }

    const timerId = setInterval(() => {
      setIntertidalYear((prevYear) => {
        const currentIndex = INTERTIDAL_YEAR_OPTIONS.findIndex(
          (option) => option.value === prevYear
        );
        const nextIndex = (currentIndex + 1) % INTERTIDAL_YEAR_OPTIONS.length;
        return INTERTIDAL_YEAR_OPTIONS[nextIndex].value;
      });
    }, 1200);

    return () => clearInterval(timerId);
  }, [isPlaying]);

  const handleDownloadClick = () => {
    if (auth) {
      handleDownload({
        type: 'api',
        url: 'intertidal-topography',
        fileName: `intertidal-topography-${intertidalYear}`,
        fileExt: 'zip',
        mimeType: 'application/zip',
        params: {
          year: intertidalYear,
        },
      });
    } else {
      SWAL_TOAST.fire({
        icon: 'error',
        title: `該功能需登入後方可使用。請先登入。`,
      });
      navigate('/login', { state: { previousPage: `${pathname}${hash}` } });
    }
  };

  return (
    <>
      <article className="l-eco-environmental-survey">
        <section className="l-eco-environmental-survey__title u-section">
          <Intro data={pageData} />
          <Tab data={pageData} />
        </section>
        <section className="l-eco-environmental-survey__site u-section">
          <div className="c-card p-3 mb-3 l-intertidal-topography__controls">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label htmlFor="intertidal-year-select" className="form-label">
                  年份
                </label>
                <select
                  id="intertidal-year-select"
                  className="form-select"
                  value={intertidalYear}
                  onChange={(e) => setIntertidalYear(e.target.value)}
                >
                  {INTERTIDAL_YEAR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-8">
                <label htmlFor="intertidal-year-slider" className="form-label">
                  年份輪播
                </label>
                <input
                  id="intertidal-year-slider"
                  type="range"
                  className="form-range l-intertidal-topography__slider"
                  min={0}
                  max={INTERTIDAL_YEAR_OPTIONS.length - 1}
                  step={1}
                  value={intertidalYearIndex >= 0 ? intertidalYearIndex : 0}
                  onChange={(e) =>
                    setIntertidalYear(
                      INTERTIDAL_YEAR_OPTIONS[Number(e.target.value)].value
                    )
                  }
                />
              </div>
              <div className="col-12 d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="e-btn e-btn--primary"
                  onClick={() => setIsPlaying((prevState) => !prevState)}
                >
                  {isPlaying ? '暫停輪播' : '開始輪播'}
                </button>
                <button
                  type="button"
                  className="e-btn e-btn--muted"
                  onClick={handleDownloadClick}
                >
                  下載 {intertidalYearLabel} 年資料
                </button>
              </div>
            </div>
          </div>
          <LeafletMap intertidalYear={intertidalYear} />
        </section>
      </article>
    </>
  );
};

export default Content;
